import { CartQueryResult } from '@graphql/queries/__generated__/cart.generated'
import React, { useState } from 'react'
import CartLinesTotal from './components/CartLinesTotal'
import { useTranslation } from 'react-i18next'
import WholesaleCartSummary from '@components/WholesaleCartSummary'
import WholesaleCartLinesContainer from '@components/WholesaleCartLines/WholesaleCartLinesContainer'
import OrderNoteForm from '@components/Forms/OrderNoteForm'
import { OrderNoteFragment } from '@graphql/fragments/__generated__/OrderNote.generated'
import FormattedDate from '@components/FormattedDate'

interface CategoryCartLine {
  pk: number
  pcs: number
  qty: number
  price: number
  weight: number
  category: string
  sortOrder: number
  currency: string
}

interface PageProps {
  cartQueryResult: CartQueryResult
  orderNotes: OrderNoteFragment[]

  cartClear(categoryId?: string): Promise<void>

  onSaveAsDraft(): Promise<void>

  onCreateOrderNote(values: { orderId: number; content: string; replyToMe: boolean }): Promise<void>

  onDeleteOrder(): void
}

const Page: React.FC<PageProps> = ({
  cartQueryResult,
  orderNotes,
  cartClear,
  onSaveAsDraft,
  onCreateOrderNote,
  onDeleteOrder,
}) => {
  const { t } = useTranslation()
  const [isNoteFormShow, setIsNoteFormShow] = useState(false)
  const onAddNote = () => {
    setIsNoteFormShow(true)
  }
  const categoriesWithLines: {
    [key: number]: CategoryCartLine[]
  } = cartQueryResult.data?.cart?.linesByCategory ? cartQueryResult.data.cart.linesByCategory : {}
  const orderId = cartQueryResult.data?.cart?.orderId
  return (
    <div className='p-0 sm:p-24 flex justify-center max-w-[1300px] mx-auto' style={{ minHeight: 'inherit' }}>
      <div className='w-full'>
        <h1 className='font-bold text-xl leading-24 mb-16 px-12 sm:px-0 mt-20 md:mt-0'>
          {t('title.shoppingBag', 'Shopping Bag')}
        </h1>
        <div className='flex flex-col md:flex-row'>
          <div className='w-full md:w-900 sm:mr-24'>
            <WholesaleCartLinesContainer />
          </div>
          <div className='w-full md:w-400 mt-20 md:mt-0 flex flex-col'>
            {!!Object.keys(categoriesWithLines).length && (
              <WholesaleCartSummary categoriesWithLines={categoriesWithLines} />
            )}
            <CartLinesTotal
              cartQueryResult={cartQueryResult}
              loading={cartQueryResult.loading}
              onSaveAsDraft={onSaveAsDraft}
              onAddNote={onAddNote}
              onDeleteOrder={onDeleteOrder}
              cartClear={cartClear}
            />
            {orderId && isNoteFormShow && (
              <OrderNoteForm
                onSubmit={async values => {
                  setIsNoteFormShow(false)
                  await onCreateOrderNote({ orderId, ...values })
                }}
                onCancel={() => setIsNoteFormShow(false)}
              />
            )}
            {!!orderNotes.length && (
              <div className='mt-10 bg-white shadow-md p-10'>
                <p className='text-bold text-base'>{t('title.notes', 'Notes')}</p>
                {orderNotes.map(node => (
                  <div key={node.id} className='pb-10 text-gray text-sm'>
                    <div>
                      {node.creator} - <FormattedDate value={node.date} variant='long' />
                    </div>
                    <pre style={{ fontFamily: 'inherit', margin: 0 }}>{node.content}</pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Page
